// WMS v2 mobile — offline mutation queue via IndexedDB (idb).
//
// Bij wifi-uitval: mutation-requests worden lokaal opgeslagen in object-store
// 'mutation_queue'. UI toont badge met aantal openstaand. Bij connect-restore
// wordt de queue in FIFO-volgorde naar de server geflusht.
//
// Serial-dedup: queue is óók een bron voor het checken van dubbele serials
// (blok 6 opdracht) — bij pick-scan checkt de UI eerst de queue voordat
// api-call gaat.
//
// LET OP: dit is een pass-through queue voor mutation-endpoints. De caller
// probeert eerst de directe fetch; als die faalt door network, wordt de
// call in de queue geplaatst en later opnieuw geprobeerd.

import { openDB, IDBPDatabase } from "idb";

const DB_NAME = "wms-scan";
const DB_VERSION = 1;
const STORE = "mutation_queue";

interface QueuedRequest {
  id?: number;
  ts: number;
  method: string;
  url: string;
  body: unknown;
  active_user_id: number | null;
  retries: number;
  last_error: string | null;
  // Optionele semantiek voor dedup / observability
  kind?: "putaway" | "pick" | "move" | "inbound" | "receive" | "other";
  serial_number?: string | null;
}

let dbP: Promise<IDBPDatabase> | null = null;
function getDb(): Promise<IDBPDatabase> {
  if (!dbP) {
    dbP = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          const s = db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
          s.createIndex("by_ts", "ts");
          s.createIndex("by_serial", "serial_number");
        }
      },
    });
  }
  return dbP;
}

export async function enqueueMutation(req: Omit<QueuedRequest, "id" | "retries" | "last_error">): Promise<number> {
  const db = await getDb();
  const id = await db.add(STORE, { ...req, retries: 0, last_error: null });
  return Number(id);
}

export async function countPending(): Promise<number> {
  const db = await getDb();
  return db.count(STORE);
}

export async function listPending(): Promise<QueuedRequest[]> {
  const db = await getDb();
  return db.getAllFromIndex(STORE, "by_ts");
}

/** Check of serie al in queue zit (nog niet-geflushed pick). Voor dedup. */
export async function serialInQueue(serial: string): Promise<QueuedRequest | null> {
  const db = await getDb();
  const rows = await db.getAllFromIndex(STORE, "by_serial", serial);
  return rows[0] ?? null;
}

/** Flush 1 batch. Returns { flushed, remaining, errors }. */
export async function flushQueue(): Promise<{ flushed: number; remaining: number; errors: number }> {
  const db = await getDb();
  const all = await db.getAllFromIndex(STORE, "by_ts");
  let flushed = 0, errors = 0;
  for (const req of all) {
    if (!req.id) continue;
    try {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      // Bearer + session-token uit localStorage (same keys als api/client.ts)
      const token = localStorage.getItem("merkatura.access_token");
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const sessionToken = localStorage.getItem("wms.active_session_token");
      if (sessionToken) headers["X-Active-Session"] = sessionToken;
      const r = await fetch(req.url, {
        method: req.method,
        headers,
        body: JSON.stringify(req.body),
      });
      if (r.ok || r.status === 409) {
        // 409 conflicts (insufficient_stock / serial already shipped) worden ALS
        // "flushed" behandeld want retry helpt niet. Foutmelding wordt gelogd.
        await db.delete(STORE, req.id);
        flushed++;
      } else if (r.status >= 500 || r.status === 0) {
        // Server-side of network → bump retries, laat staan
        await db.put(STORE, { ...req, retries: (req.retries ?? 0) + 1, last_error: `HTTP ${r.status}` });
        errors++;
      } else {
        // 4xx anders dan 409: waarschijnlijk permanent (bad request); verwijder
        await db.delete(STORE, req.id);
        errors++;
      }
    } catch (e) {
      await db.put(STORE, { ...req, retries: (req.retries ?? 0) + 1, last_error: e instanceof Error ? e.message : String(e) });
      errors++;
    }
  }
  const remaining = await db.count(STORE);
  return { flushed, remaining, errors };
}

/** Register: automatisch flush wanneer online. */
let onlineHandlerRegistered = false;
export function registerOnlineFlush(): void {
  if (onlineHandlerRegistered) return;
  onlineHandlerRegistered = true;
  window.addEventListener("online", () => { void flushQueue(); });
  // Ook periodiek proberen elke 30s
  window.setInterval(() => { if (navigator.onLine) void flushQueue(); }, 30_000);
}

/** Mutex + flush-lock voor concurrency safety van flushQueue. */
let flushInProgress = false;
export async function safeFlush(): Promise<void> {
  if (flushInProgress) return;
  flushInProgress = true;
  try { await flushQueue(); }
  finally { flushInProgress = false; }
}

// ---------------------------------------------------------------------------
// runMutation — probeer direct; bij network-fail (fetch throws) enqueueMutation
// zodat de mutatie later doorkomt. Return kort-samengevat resultaat.
//
// Semantiek:
//   - HTTP OK: return { ok: true, queued: false, response: <parsed body> }
//   - HTTP 4xx/5xx (server responded): throw ApiError-achtige — caller ziet 't
//   - Network-fail (fetch itself throws): enqueue + return { ok: true, queued: true }
//     Server processed het later.
//
// De caller (WmsPutawayFlow etc.) hoeft alleen te weten dat de mutatie
// "geregistreerd" is; UI markeert dit als success (badge count +=1).
// ---------------------------------------------------------------------------

interface RunMutationArgs {
  kind: QueuedRequest["kind"];
  url: string;
  method: string;
  body: unknown;
  activeUserId: number | null;
  serialNumber?: string | null;
}

export async function runMutation<T>(args: RunMutationArgs): Promise<{ ok: boolean; queued: boolean; response: T | null; error?: string }> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = localStorage.getItem("merkatura.access_token");
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const sessionToken = localStorage.getItem("wms.active_session_token");
  if (sessionToken) headers["X-Active-Session"] = sessionToken;

  try {
    const res = await fetch(args.url, {
      method: args.method,
      headers,
      body: JSON.stringify(args.body),
    });
    if (res.ok || res.status === 201) {
      const ct = res.headers.get("content-type") || "";
      const parsed = ct.includes("application/json") ? await res.json() : null;
      return { ok: true, queued: false, response: parsed };
    }
    // Server-response 4xx/5xx → NIET queueen (retry helpt niet, is bad request).
    const ct = res.headers.get("content-type") || "";
    const errorBody = ct.includes("application/json") ? await res.json() : { error: await res.text() };
    return { ok: false, queued: false, response: null, error: errorBody?.detail ?? errorBody?.error ?? `HTTP ${res.status}` };
  } catch (e) {
    // fetch throw = network offline / server unreachable → queue
    await enqueueMutation({
      ts: Date.now(),
      method: args.method,
      url: args.url,
      body: args.body,
      active_user_id: args.activeUserId,
      kind: args.kind,
      serial_number: args.serialNumber ?? null,
    });
    return { ok: true, queued: true, response: null };
  }
}
